/** Column is the spine level, never this view's depth. */
import type { SpineLevel } from "../domain/studioTypes";
import type { StudioTree, StudioTreeNode } from "./treeProjection";

export const RUNG_PREFIX = "rung:";

export const rungIdOf = (scopeId: string, levelIndex: number): string =>
  `${RUNG_PREFIX}${levelIndex}:${scopeId}`;

export const rungPartsOf = (id: string): { scopeId: string; levelIndex: number } | null => {
  if (!id.startsWith(RUNG_PREFIX)) return null;
  const rest = id.slice(RUNG_PREFIX.length);
  const colon = rest.indexOf(":");
  if (colon < 0) return null;
  const levelIndex = Number(rest.slice(0, colon));
  if (!Number.isInteger(levelIndex)) return null;
  return { scopeId: rest.slice(colon + 1), levelIndex };
};

/** Separate expanded key so one set can hold the chain and the value list. */
export const valuesKeyOf = (rungId: string): string => `${rungId}::values`;

export interface LadderNode {
  id: string;
  kind: "value" | "level";
  column: number;
  parentId: string | null;
  childIds: string[];
  tree: StudioTreeNode | null;
  levelIndex: number;
  levelLabel: string;
  scopeId: string;
  canChain: boolean;
  canListValues: boolean;
  canLift: boolean;
}

export interface LadderView {
  nodesById: Record<string, LadderNode>;
  rootIds: string[];
}

export const buildLadderView = (
  tree: StudioTree,
  spine: readonly SpineLevel[],
  expanded: ReadonlySet<string>,
): LadderView => {
  const nodesById: Record<string, LadderNode> = {};

  /** Not `levelIndex + 1` — branches skip levels that split nothing. */
  const nextLevelIn = (scope: StudioTreeNode, levelIndex: number): number | undefined =>
    scope.branchLevels.find((index) => index > levelIndex);

  const addRung = (scope: StudioTreeNode, levelIndex: number, parentId: string): string | null => {
    // Re-ranked branch holds a different attribute at this global index.
    const level = scope.branchSpine[levelIndex] ?? spine[levelIndex];
    if (!level) return null;
    const id = rungIdOf(scope.id, levelIndex);
    const firstLevel = scope.branchLevels[0] ?? scope.depth + 1;
    const firstRung = levelIndex === firstLevel;
    const next = nextLevelIn(scope, levelIndex);
    const node: LadderNode = {
      id,
      kind: "level",
      column: levelIndex,
      parentId,
      childIds: [],
      tree: null,
      levelIndex,
      levelLabel: level.label,
      scopeId: scope.id,
      canChain: next !== undefined,
      // Deeper rungs would flatten branches together.
      canListValues: firstRung && scope.childIds.length > 0,
      canLift: levelIndex > firstLevel,
    };
    nodesById[id] = node;

    if (next !== undefined && expanded.has(id)) {
      const chained = addRung(scope, next, id);
      if (chained) node.childIds.push(chained);
    }
    if (node.canListValues && expanded.has(valuesKeyOf(id))) {
      for (const childId of scope.childIds) {
        const child = addValue(childId, id);
        if (child) node.childIds.push(child);
      }
    }
    return id;
  };

  const addValue = (treeId: string, parentId: string | null): string | null => {
    // Content-path ids go stale across a rebuild.
    const treeNode = tree.nodesById[treeId];
    if (!treeNode) return null;
    const node: LadderNode = {
      id: treeId,
      kind: "value",
      column: treeNode.depth,
      parentId,
      childIds: [],
      tree: treeNode,
      levelIndex: treeNode.depth,
      levelLabel: treeNode.levelLabel,
      scopeId: treeNode.parentId ?? "",
      canChain: false,
      canListValues: false,
      canLift: false,
    };
    nodesById[treeId] = node;
    if (treeNode.childIds.length > 0 && expanded.has(treeId)) {
      const rung = addRung(treeNode, treeNode.branchLevels[0] ?? treeNode.depth + 1, treeId);
      if (rung) node.childIds.push(rung);
    }
    return treeId;
  };

  const rootIds = tree.rootIds.flatMap((rootId) => addValue(rootId, null) ?? []);
  return { nodesById, rootIds };
};

export const canOpenLadderNode = (node: LadderNode): boolean =>
  node.kind === "value"
    ? (node.tree?.childIds.length ?? 0) > 0
    : node.canChain || node.canListValues;
